import { GoalRequired } from '@/components/GoalRequired';
import { ALLERGY_NOTICE } from '@/pages/onboarding/Questionnaire';
import { MealRecommendation } from '@/components/MealPreferences';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Check, UtensilsCrossed, ChevronRight, ChevronDown, ChefHat, Plus, Trash2, X, Edit2, Camera, Lock, RefreshCw, Clock, Flame, Repeat, Info } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { mealImage, GENERIC_MEAL_IMAGE } from '@/lib/mealImages';
import { useProgramLocked, LockedScreen } from '@/components/ProgramLock';
import { ProgramHeader } from '@/components/ProgramHeader';
import { NutritionTargets } from '@/components/NutritionTargets';
import { SearchPicker } from '@/components/SearchPicker';
import { MealTemplates } from '@/components/MealTemplates';
import { AppTour } from '@/components/AppTour';
import { ScanMeal } from '@/components/ScanMeal';
import { BookMarked } from 'lucide-react';
import { EditPlanMeal } from '@/components/EditPlanMeal';

const CATEGORY_ORDER = [
  { id: 'Vegetable', label: 'Vegetables', step: 1, color: '#65A30D', required: true },
  { id: 'Protein',   label: 'Protein',    step: 2, color: '#FF6FA8', required: true },
  { id: 'Carbohydrate', label: 'Carbs',  step: 3, color: '#FFB800', required: false },
  { id: 'Fruit',     label: 'Fruit',      step: 4, color: '#FF8A4C', required: false },
  { id: 'Fat',       label: 'Healthy Fat',step: 5, color: '#7DD3FC', required: false },
];

export default function Nutrition() {
  const locked = useProgramLocked();
  const { user } = useAuth();
  const nav = useNavigate();
  const isTracker = (user?.subscription_tier || '') === 'tracker';
  const today = format(new Date(), 'yyyy-MM-dd');
  const [assignments, setAssignments] = useState([]);
  const [builtMeals, setBuiltMeals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [foods, setFoods] = useState([]);
  const [editing, setEditing] = useState(null); // built meal being edited
  const [detailMeal, setDetailMeal] = useState(null);

  const load = async () => {
    const [a, b, s, f] = await Promise.all([
      api.get('/meal-plans/assignments'),
      api.get('/built-meals', { params: { date: today } }),
      api.get('/nutrition/daily-summary'),
      api.get('/foods', { params: { enabled_only: true } }),
    ]);
    setAssignments(a.data); setBuiltMeals(b.data); setSummary(s.data); setFoods(f.data);
  };
  useEffect(() => { if (!locked) load(); }, []);

  const todayPlan = assignments.find(a => a.date === today);

  if (locked) return <LockedScreen title="Diet" color='#65A30D' />;
  return (
    <div className="space-y-4 page-grid" data-testid="client-nutrition">
      <div>
        <div className="label-mini text-[#FFB800]">Pillar 1</div>
        <div className="flex items-end justify-between gap-3">
          <h1 className="font-display font-black text-3xl mt-1">Nutrition</h1>
          <AppTour />
        </div>
      </div>

      <GoalRequired />
      <ProgramHeader type="nutrition" />
      <p className="text-[11px] leading-relaxed text-muted-foreground rounded-xl border border-[#FFB800]/40 bg-[#FFB800]/10 px-3 py-2" data-testid="meal-plan-allergy-notice">{ALLERGY_NOTICE}</p>

      {/* Daily dashboard */}
      {summary && <DailyDashboard s={summary} />}

      <Tabs defaultValue="plan">
        <TabsList className="bg-regen-card border border-[var(--c-hair)] w-full">
          <TabsTrigger value="plan" className="flex-1 font-semibold" data-testid="nu-tab-plan">My Meal Plan</TabsTrigger>
          <TabsTrigger value="templates" className="flex-1" data-testid="nu-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="builder" className="flex-1" data-testid="nu-tab-builder">Build a Meal</TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="mt-4 space-y-4">
          <p className="text-xs text-muted-foreground" data-testid="builder-explainer">Log or create a meal that isn't in the YourReGen Meal Library — e.g. food eaten out.</p>
          <MealBuilder foods={foods} editing={editing} onCancelEdit={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
          {builtMeals.length > 0 && (
            <div>
              <div className="label-mini mt-4 mb-2">Today's Logged Meals</div>
              <div className="space-y-2">
                {builtMeals.map(m => <BuiltMealRow key={m.id} meal={m} onClick={() => setDetailMeal(m)} />)}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-4 space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed regen-card p-3" data-testid="templates-explainer">Templates are reusable meals created by your coach. You can adjust the portion of each ingredient before logging, including entering 0 g if you don't eat an ingredient. Your calories and macros will update automatically.</p>
          <MealTemplates foods={foods} reload={load} />
        </TabsContent>

        <TabsContent value="plan" className="mt-4 space-y-4 pb-24">
          <p className="text-xs text-muted-foreground" data-testid="plan-explainer">Your personalised plan from Coach JP — follow it, swap a meal, or log something else.</p>
          {todayPlan && <MealPlanCard plan={todayPlan} foods={foods} onUpdate={load} />}
          <ProgramBrowser assignments={assignments} today={today} reload={load} />
        </TabsContent>
      </Tabs>

      {/* Meal Scanner — YourReGen+ feature */}
      {isTracker ? <ScanMealUpsell onUpgrade={() => nav('/choose-journey')} /> : <ScanMeal reload={load} />}

      {/* Nutrition targets + extra food logging */}
      <NutritionTargets summary={summary} reload={load} />

      <BuiltMealDetail
        meal={detailMeal}
        onClose={() => setDetailMeal(null)}
        onEdit={(m) => { setDetailMeal(null); setEditing(m); }}
        reload={load}
      />
    </div>
  );
}

function ScanMealUpsell({ onUpgrade }) {
  return (
    <button onClick={onUpgrade} data-testid="scan-meal-upsell"
      className="w-full text-left regen-card-elevated p-4 flex items-center gap-3 active:scale-[0.99] transition border border-[#00C8FF]/20">
      <div className="w-11 h-11 rounded-xl bg-[#00C8FF]/15 flex items-center justify-center flex-shrink-0 relative">
        <Camera className="w-5 h-5 text-[#00C8FF]" />
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--c-elev2)] border border-[var(--c-hair)] flex items-center justify-center"><Lock className="w-2.5 h-2.5 text-[#00C8FF]" /></span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">Scan Meals & Track Nutrition</div>
        <div className="text-xs text-muted-foreground">Snap a photo to log food instantly — start your 7-day YourReGen+ free trial.</div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
    </button>
  );
}

function DailyDashboard({ s }) {
  return (
    <div className="regen-card-elevated p-4" data-testid="nutrition-dashboard">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mini">Today's Logged Meals</div>
        <span className="pill bg-[#FFB800]/10 text-[#FFB800]">{s.meals_logged} logged</span>
      </div>
      <div className="grid grid-cols-5 gap-2 mb-3">
        <Stat label="Cal" value={Math.round(s.totals.calories)} color="#FFB800" />
        <Stat label="Prot" value={Math.round(s.totals.protein)} unit="g" color='#65A30D' />
        <Stat label="Carbs" value={Math.round(s.totals.carbs)} unit="g" color="#00C8FF" />
        <Stat label="Fat" value={Math.round(s.totals.fat)} unit="g" color="#7DD3FC" />
        <Stat label="Fiber" value={Math.round(s.totals.fiber)} unit="g" color="#FF6FA8" />
      </div>
      <div className="text-xs text-muted-foreground">From meals you built today{s.meal_plan_total > 0 && ` · Meal plan ${s.meal_plan_compliance_pct}%`}</div>
    </div>
  );
}

function Stat({ label, value, unit = '', target, color }) {
  const pct = target ? Math.min(100, (value / target) * 100) : 0;
  return (
    <div className="text-center">
      <div className="font-mono text-lg leading-none" style={{ color }}>{value}{unit}</div>
      <div className="label-mini mt-0.5">{label}{target && <span className="text-muted-foreground"> / {target}</span>}</div>
      {target && <div className="mt-1 h-0.5 bg-[var(--c-film)] rounded-full overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: color }} /></div>}
    </div>
  );
}

function BuiltMealRow({ meal, onClick }) {
  const t = meal.totals || {};
  return (
    <button onClick={onClick} data-testid={`built-${meal.id}`}
      className="w-full regen-card-elevated p-3 flex gap-3 items-center text-left active:scale-[0.99] transition">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meal.completed ? 'bg-[var(--c-green-soft)]' : 'bg-[#FFB800]/15'}`}>
        {meal.completed ? <Check className="w-4 h-4 text-[var(--c-green-ink)]" /> : <ChefHat className="w-4 h-4 text-[#FFB800]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium truncate">{meal.name || meal.slot}</div>
          {meal.completed && <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[9px]">DONE</span>}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">{(meal.items || []).map(i => i.food?.name).filter(Boolean).join(' · ') || '—'}</div>
        <div className="text-[11px] mt-1 flex gap-2 font-mono">
          <span className="text-[#FFB800]">{Math.round(t.calories || 0)} kcal</span>
          <span className="text-[var(--c-green-ink)]">P {Math.round(t.protein || 0)}</span>
          <span className="text-[#00C8FF]">C {Math.round(t.carbs || 0)}</span>
          <span className="text-[#7DD3FC]">F {Math.round(t.fat || 0)}</span>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
    </button>
  );
}

function BuiltMealDetail({ meal, onClose, onEdit, reload }) {
  if (!meal) return null;
  const t = meal.totals || {};

  const complete = async () => {
    await api.post(`/built-meals/${meal.id}/complete`);
    toast.success('Marked complete'); onClose(); reload();
  };
  const del = async () => {
    if (!confirm('Delete this meal?')) return;
    await api.delete(`/built-meals/${meal.id}`);
    toast.success('Deleted'); onClose(); reload();
  };

  return (
    <Dialog open={!!meal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md lg:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="sr-only"><DialogTitle>{meal.name}</DialogTitle></DialogHeader>
        <div className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-mini text-[#FFB800]">{meal.slot}</div>
              <div className="font-display font-black text-2xl mt-1">{meal.name}</div>
            </div>
            {meal.completed && <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)]"><Check className="w-3 h-3 mr-1" /> Done</span>}
          </div>

          <div className="grid grid-cols-5 gap-2 mt-4">
            <Macro label="Cal" value={Math.round(t.calories || 0)} color="#FFB800" />
            <Macro label="P" value={`${Math.round(t.protein || 0)}g`} color='#65A30D' />
            <Macro label="C" value={`${Math.round(t.carbs || 0)}g`} color="#00C8FF" />
            <Macro label="F" value={`${Math.round(t.fat || 0)}g`} color="#7DD3FC" />
            <Macro label="Fbr" value={`${Math.round(t.fiber || 0)}g`} color="#FF6FA8" />
          </div>

          {meal.tags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {meal.tags.map(t => <span key={t} className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[10px]">{t}</span>)}
            </div>
          )}

          <div className="mt-5">
            <div className="label-mini">Foods</div>
            <div className="mt-2 space-y-2">
              {(meal.items || []).map((it, idx) => (
                <div key={idx} className="regen-card p-2.5 flex items-center gap-3">
                  {it.food?.photo_url ? <img src={it.food.photo_url} alt={it.food.name} className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-[var(--c-film)]" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.food?.name}</div>
                    <div className="text-[10px] text-muted-foreground">{it.food?.category}</div>
                  </div>
                  <div className="font-mono text-xs text-[#FFB800]">{it.grams} g</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-5">
            {!meal.completed && (
              <Button onClick={complete} data-testid="meal-complete" className="bg-[var(--c-green)] text-[#081726] font-semibold h-11"><Check className="w-4 h-4 mr-1" /> Mark Complete</Button>
            )}
            <Button onClick={() => onEdit(meal)} variant="outline" data-testid="meal-edit" className="border-[var(--c-hair)] h-11"><Edit2 className="w-4 h-4 mr-1" /> Edit</Button>
            <Button onClick={del} variant="outline" data-testid="meal-delete" className={`border-red-500/30 text-red-400 h-11 ${meal.completed ? '' : 'col-span-1'}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Macro({ label, value, color }) {
  return (
    <div className="regen-card p-2 text-center">
      <div className="font-mono text-sm" style={{ color }}>{value}</div>
      <div className="label-mini mt-0.5 text-[9px]">{label}</div>
    </div>
  );
}

function MealBuilder({ foods, editing, onCancelEdit, onSaved }) {
  const [slot, setSlot] = useState('Meal 1');
  const [name, setName] = useState('');
  const [items, setItems] = useState([]); // { food_id, grams }
  const [picked, setPicked] = useState(null);
  const [grams, setGramsInput] = useState('');
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState('');

  useEffect(() => {
    if (editing) {
      setSlot(editing.slot);
      setName(editing.name);
      setItems((editing.items || []).map(i => ({ food_id: i.food_id, grams: i.grams })));
    } else {
      setSlot('Meal 1'); setName(''); setItems([]);
    }
  }, [editing]);

  const foodById = (id) => foods.find(f => f.id === id);

  const totals = useMemo(() => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
    items.forEach(it => {
      const f = foodById(it.food_id); if (!f) return;
      const g = (parseFloat(it.grams) || 0) / 100;
      t.calories += (f.calories_per_100g || 0) * g;
      t.protein += (f.protein_per_100g || 0) * g;
      t.carbs += (f.carbs_per_100g || 0) * g;
      t.fat += (f.fat_per_100g || 0) * g;
      t.fiber += (f.fiber_per_100g || 0) * g;
    });
    return t;
  }, [items, foods]);

  const dbMatch = picked?.id ? picked : foods.find(f => f.name.toLowerCase() === (picked?.name || '').toLowerCase());
  const g = parseFloat(grams) || 0;
  const previewCal = dbMatch ? Math.round((dbMatch.calories_per_100g || 0) * g / 100) : 0;

  const addFood = () => {
    if (!dbMatch) return toast.error('Pick a food from the list');
    if (g <= 0) return toast.error('Enter grams');
    setItems([...items, { food_id: dbMatch.id, grams: g }]);
    setPicked(null); setGramsInput('');
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const setItemGrams = (idx, val) => setItems(items.map((it, i) => i === idx ? { ...it, grams: parseFloat(val) || 0 } : it));

  const save = async () => {
    if (items.length === 0) return toast.error('Add at least one food');
    const payload = {
      slot, name: name || slot,
      items: items.map(i => ({ food_id: i.food_id, grams: parseFloat(i.grams) || 0 })),
    };
    try {
      if (editing) await api.put(`/built-meals/${editing.id}`, payload);
      else await api.post('/built-meals', payload);
      toast.success(editing ? 'Updated' : `${slot} logged`);
      onSaved?.();
    } catch { toast.error('Failed'); }
  };

  const saveTemplate = async () => {
    if (items.length === 0) return toast.error('Add foods first');
    if (!tplName.trim()) return toast.error('Name your template');
    try {
      await api.post('/meal-presets', {
        name: tplName.trim(), scope: 'personal', slot,
        items: items.map(i => ({ food_id: i.food_id, grams: parseFloat(i.grams) || 0 })),
      });
      toast.success(`Template "${tplName.trim()}" saved`);
      setTplOpen(false); setTplName('');
    } catch { toast.error('Failed to save template'); }
  };

  return (
    <div className="space-y-3">
      <div className="regen-card-elevated p-4 bg-gradient-to-br from-[#FFB800]/10 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ChefHat className="w-6 h-6 text-[#FFB800]" />
            <div>
              <div className="font-display font-bold">{editing ? 'Edit Meal' : 'Build A Meal'}</div>
              <div className="text-xs text-muted-foreground">Search any food, set grams. Macros auto-calculate.</div>
            </div>
          </div>
          {editing && <button onClick={onCancelEdit} className="w-8 h-8 rounded-full hover:bg-[var(--c-film)] flex items-center justify-center"><X className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Live totals */}
      <div className="regen-card-elevated p-3 sticky top-0 z-10">
        <div className="grid grid-cols-5 gap-2">
          <Macro label="Cal" value={Math.round(totals.calories)} color="#FFB800" />
          <Macro label="P" value={`${Math.round(totals.protein)}g`} color='#65A30D' />
          <Macro label="C" value={`${Math.round(totals.carbs)}g`} color="#00C8FF" />
          <Macro label="F" value={`${Math.round(totals.fat)}g`} color="#7DD3FC" />
          <Macro label="Fbr" value={`${Math.round(totals.fiber)}g`} color="#FF6FA8" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Slot</Label>
          <Select value={slot} onValueChange={setSlot}>
            <SelectTrigger className="bg-regen-card border-[var(--c-hair)] h-10" data-testid="builder-slot"><SelectValue /></SelectTrigger>
            <SelectContent>{['Meal 1', 'Meal 2', 'Meal 3', 'Snack'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Meal Name</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="My Lunch" data-testid="builder-name" className="bg-regen-card border-[var(--c-hair)] h-10" />
        </div>
      </div>

      {/* Searchable food add */}
      <div className="regen-card-elevated p-3 space-y-2.5">
        <div className="label-mini">Add Food</div>
        <SearchPicker
          items={foods}
          value={picked?.name || ''}
          onPick={(v) => { setPicked(v); if (v?.serving_grams) setGramsInput(String(v.serving_grams)); }}
          placeholder="Search food (Chicken, Rice, Greek Yogurt…)"
          testid="builder-food-search"
        />
        {dbMatch && (
          <div data-testid="builder-add-row">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input type="number" value={grams} onChange={e => setGramsInput(e.target.value)} placeholder="grams" data-testid="builder-grams" className="bg-[var(--c-film)] border-[var(--c-hair)] h-11 rounded-xl font-mono pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">g</span>
              </div>
              <Button onClick={addFood} data-testid="builder-add-food-btn" className="h-11 px-4 bg-[#FFB800] text-[#081726] font-semibold rounded-xl"><Plus className="w-4 h-4" /></Button>
            </div>
            {g > 0 && (
              <div className="mt-2 flex items-center gap-2 text-sm rounded-xl bg-[#FFB800]/10 px-3 py-2">
                <span className="font-medium">{dbMatch.name}</span>
                <span className="text-muted-foreground">— {g}g —</span>
                <span className="font-mono font-bold text-[#FFB800]">{previewCal} kcal</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Added items */}
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((it, idx) => {
            const f = foodById(it.food_id);
            const gg = (parseFloat(it.grams) || 0) / 100;
            return (
              <div key={idx} className="regen-card-elevated p-2.5 flex items-center gap-3" data-testid={`builder-item-${idx}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f?.name || '—'}</div>
                  <div className="text-[11px] font-mono text-muted-foreground">
                    {Math.round((f?.calories_per_100g || 0) * gg)} kcal · P{Math.round((f?.protein_per_100g || 0) * gg)} C{Math.round((f?.carbs_per_100g || 0) * gg)} F{Math.round((f?.fat_per_100g || 0) * gg)}
                  </div>
                </div>
                <div className="relative w-20">
                  <Input type="number" value={it.grams} onChange={e => setItemGrams(idx, e.target.value)} data-testid={`builder-item-grams-${idx}`} className="bg-transparent border-[var(--c-hair)] font-mono text-center h-9 pr-6" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">g</span>
                </div>
                <button onClick={() => removeItem(idx)} data-testid={`builder-item-remove-${idx}`} className="text-muted-foreground hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      )}

      <Button onClick={save} data-testid="log-meal-btn" className="w-full h-12 bg-[#FFB800] text-[#081726] font-semibold rounded-xl">
        {editing ? <><Check className="w-4 h-4 mr-2" /> Update Meal</> : <><Plus className="w-4 h-4 mr-2" /> Log Meal</>}
      </Button>
      {!editing && items.length > 0 && (
        <Button onClick={() => setTplOpen(true)} variant="outline" data-testid="save-template-btn" className="w-full h-11 border-[var(--c-hair)] rounded-xl">
          <BookMarked className="w-4 h-4 mr-2" /> Save as Template
        </Button>
      )}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-sm">
          <DialogHeader><DialogTitle>Save Meal Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Reuse this meal in one tap next time (e.g. "My Breakfast", "Work Lunch").</p>
            <Input value={tplName} onChange={e => setTplName(e.target.value)} placeholder="Template name" data-testid="template-name-input" className="bg-[var(--c-film)] border-[var(--c-hair)] h-11" />
            <Button onClick={saveTemplate} data-testid="confirm-save-template" className="w-full h-11 bg-[#FFB800] text-[#081726] font-semibold"><BookMarked className="w-4 h-4 mr-2" /> Save Template</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MealPlanCard({ plan, foods = [], onUpdate }) {
  const [snaps, setSnaps] = useState(plan.meal_snapshots || []);
  const [editIdx, setEditIdx] = useState(null);
  const [swapping, setSwapping] = useState(null);
  const [proteinPick, setProteinPick] = useState(null); // meal idx whose protein-swap dialog is open
  const [proteinOpts, setProteinOpts] = useState(null);
  const [changing, setChanging] = useState(false);
  const toggleMeal = async (idx) => {
    const next = !snaps[idx]?.done;
    const updated = snaps.map((s, i) => i === idx ? { ...s, done: next } : s);
    setSnaps(updated);
    try {
      await api.post('/meal-plans/complete-meal', { assignment_id: plan.id, index: idx, done: next });
      toast.success(next ? `${snaps[idx]?.slot} logged` : 'Marked not eaten'); onUpdate?.();
    } catch { toast.error('Failed'); setSnaps(snaps); }
  };
  const [swapOpts, setSwapOpts] = useState(null); // { idx, data }
  const swapMeal = async (idx) => {
    setSwapping(idx);
    try {
      const r = await api.get('/meal-plans/swap-options', { params: { assignment_id: plan.id, index: idx } });
      setSwapOpts({ idx, data: r.data });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load alternatives'); }
    finally { setSwapping(null); }
  };
  const chooseSwap = async (mealId) => {
    const idx = swapOpts.idx;
    try {
      const r = await api.post('/meal-plans/swap-meal', { assignment_id: plan.id, index: idx, meal_id: mealId });
      setSnaps(prev => prev.map((s, i) => i === idx ? { slot: s.slot, meal: r.data.meal, done: false, client_choice: true } : s));
      const d = r.data.day_totals;
      if (r.data.warnings?.length) toast.warning(r.data.warnings.join(' '));
      else toast.success(`Swapped · day now ${d.calories} / ${r.data.target_kcal} kcal · ${d.protein_g} g protein`);
      setSwapOpts(null); onUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not swap meal'); }
  };
  const openProteinPicker = async (idx) => {
    setProteinPick(idx);
    if (!proteinOpts) {
      try { const r = await api.get('/meal-plans/protein-options'); setProteinOpts(r.data.options || []); }
      catch { setProteinOpts([]); }
    }
  };
  const changeProtein = async (foodId) => {
    const idx = proteinPick;
    setChanging(true);
    try {
      const r = await api.post('/meal-plans/change-protein', { assignment_id: plan.id, index: idx, food_id: foodId });
      setSnaps(prev => prev.map((s, i) => i === idx ? { slot: s.slot, meal: r.data.meal, done: false } : s));
      toast.success('Protein swapped — macros recalculated'); setProteinPick(null); onUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change protein'); }
    finally { setChanging(false); }
  };
  const doneCount = snaps.filter(s => s.done).length;

  return (
    <div className="space-y-3 pb-4" data-testid="meal-plan-list">
      <div className="regen-card-elevated p-5 flex items-center justify-between">
        <div>
          <div className="label-mini">Today's Plan</div>
          <div className="font-display font-bold text-xl mt-1">{plan.plan_snapshot?.name}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl text-[var(--c-green-ink)]" data-testid="meal-plan-progress">{doneCount}/{snaps.length}</div>
          <div className="text-[10px] text-muted-foreground uppercase">meals eaten</div>
        </div>
      </div>
      {snaps.map((item, idx) => (
        <div key={idx} className={`regen-card-elevated p-3 transition ${item.done ? 'opacity-70' : ''}`} data-testid={`meal-${idx}`}>
          <div className="flex gap-3">
            <img src={mealImage(item.meal)} alt={item.meal?.name} onError={(e) => { e.currentTarget.src = GENERIC_MEAL_IMAGE; }} className="w-20 h-20 rounded-xl object-cover bg-[var(--c-film)]" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="label-mini text-[#FFB800]">{item.slot}</div>
                {item.done && <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[9px]"><Check className="w-2.5 h-2.5 mr-0.5 inline" />EATEN</span>}
                {item.meal?.swapped && <span className="pill bg-[#00C8FF]/10 text-[#00C8FF] text-[9px]">SWAPPED</span>}
                {item.meal?.client_edited && <span className="pill bg-[#FFB800]/10 text-[#FFB800] text-[9px]" data-testid={`meal-edited-${idx}`}>EDITED</span>}
              </div>
              <div className={`font-display font-bold mt-0.5 ${item.done ? 'line-through text-muted-foreground' : ''}`}>{item.meal?.name}</div>
              {item.meal?.ingredients && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{item.meal.ingredients}</div>}
              <div className="mt-2 flex gap-2 text-xs flex-wrap items-center font-mono">
                <span className="pill bg-[#FFB800]/10 text-[#FFB800]">{Math.round(item.meal?.calories || 0)} kcal</span>
                <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)]">P {Math.round(item.meal?.protein_g || 0)}</span>
                <span className="pill bg-[#00C8FF]/10 text-[#00C8FF]">C {Math.round(item.meal?.carbs_g || 0)}</span>
                <span className="pill bg-[#7DD3FC]/10 text-[#7DD3FC]">F {Math.round(item.meal?.fat_g || 0)}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <button onClick={() => toggleMeal(idx)} data-testid={`meal-complete-${idx}`}
              className={`rounded-xl px-3 py-2.5 text-xs font-semibold transition ${item.done ? 'bg-[var(--c-film)] text-muted-foreground' : 'bg-[var(--c-green)] text-[#081726]'}`}>
              {item.done ? 'Undo' : "I've Eaten This"}
            </button>
            <button onClick={() => swapMeal(idx)} disabled={swapping === idx} data-testid={`meal-swap-${idx}`}
              className="rounded-xl px-3 py-2.5 text-xs font-semibold border border-[#00C8FF]/30 text-[#00C8FF] disabled:opacity-50 flex items-center justify-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${swapping === idx ? 'animate-spin' : ''}`} /> {swapping === idx ? 'Swapping…' : 'Swap Meal'}
            </button>
            <button onClick={() => setEditIdx(idx)} disabled={!(item.meal?.ingredients_list || []).length} data-testid={`meal-edit-${idx}`}
              className="rounded-xl px-3 py-2.5 text-xs font-semibold border border-[#FFB800]/40 text-[#FFB800] disabled:opacity-40 flex items-center justify-center gap-1.5">
              <Edit2 className="w-3.5 h-3.5" /> Edit Meal
            </button>
          </div>
        </div>
      ))}
      <p className="text-[11px] text-muted-foreground text-center px-4">Tap <span className="text-[#00C8FF]">Swap Meal</span> to choose another coach-approved recipe (portions and macros recalculate to fit your day), or log exactly what you ate under <span className="text-[#FFB800]">Build a Meal</span>.</p>

      <EditPlanMeal open={editIdx !== null} onOpenChange={(o) => !o && setEditIdx(null)} plan={plan} idx={editIdx} snap={snaps[editIdx]} foods={foods}
        onSaved={(meal) => { const i = editIdx; setSnaps(prev => prev.map((s, k) => (k === i ? { ...s, meal } : s))); onUpdate?.(); }} />

      <Dialog open={swapOpts !== null} onOpenChange={(o) => !o && setSwapOpts(null)}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md max-h-[80vh] overflow-y-auto" data-testid="swap-options-dialog">
          <DialogHeader><DialogTitle>Swap {swapOpts?.data?.category} meal</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Coach-approved recipes, portioned to keep your day near {swapOpts?.data?.target_kcal} kcal.</p>
          {swapOpts?.data?.options?.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground" data-testid="swap-no-options">{swapOpts.data.message}</div>}
          <div className="space-y-2">
            {(swapOpts?.data?.options || []).map(o => (
              <button key={o.meal_id} onClick={() => chooseSwap(o.meal_id)} data-testid={`swap-option-${o.meal_id}`}
                className="w-full text-left rounded-xl border border-[var(--c-hair)] p-3 hover:border-[#00C8FF]/50 transition">
                <div className="flex justify-between gap-2 text-sm"><span className="font-semibold">{o.name}</span><span className="font-mono text-xs">{o.calories} kcal · {o.protein_g} g P</span></div>
                <div className="text-[11px] text-muted-foreground mt-1">{o.ingredients}</div>
                <div className={`text-[11px] mt-1 font-mono ${o.warnings.length ? 'text-[#FFB800]' : 'text-[var(--c-green-ink)]'}`}>Day after swap: {o.day_totals.calories} kcal · {o.day_totals.protein_g} g protein{o.warnings.length ? ` — ${o.warnings.join(' ')}` : ''}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={proteinPick !== null} onOpenChange={(o) => !o && setProteinPick(null)}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-sm max-h-[80vh] overflow-y-auto" data-testid="protein-picker-dialog">
          <DialogHeader><DialogTitle>Change Protein</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Keeps your carbs & veg the same — we recalculate the new protein's portion to match this meal's calories.</p>
          {proteinOpts === null ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading proteins…</div>
          ) : proteinOpts.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No protein options available.</div>
          ) : (
            <div className="space-y-1.5">
              {proteinOpts.map(o => (
                <button key={o.id} data-testid={`protein-option-${o.id}`} disabled={changing} onClick={() => changeProtein(o.id)}
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-[var(--c-hair)] hover:bg-[var(--c-film)] disabled:opacity-50 text-left transition">
                  <span className="font-medium text-sm">{o.name}</span>
                  <span className="text-[11px] text-muted-foreground font-mono">{Math.round(o.protein_per_100g)}g P · {Math.round(o.calories_per_100g)} kcal /100g</span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function dayTotals(snaps) {
  return (snaps || []).reduce((t, s) => {
    const m = s.meal || {};
    t.cal += m.calories || 0; t.p += m.protein_g || 0; t.c += m.carbs_g || 0; t.f += m.fat_g || 0;
    return t;
  }, { cal: 0, p: 0, c: 0, f: 0 });
}

function ProgramBrowser({ assignments, today, reload }) {
  const [overview, setOverview] = useState(null);
  const [week, setWeek] = useState(null);
  const [detail, setDetail] = useState(null); // { meal, slot }
  const [regenerating, setRegenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadOverview = () => api.get('/nutrition/my-program')
    .then(r => { setOverview(r.data); if (r.data?.has_program) setWeek(r.data.current_week || 1); return r.data; })
    .catch(() => { setOverview({ has_program: false }); return null; });

  useEffect(() => { loadOverview(); }, []);

  // Kick off an on-demand regeneration, then poll until the new plan is ready.
  const startRegenerate = async () => {
    setConfirmOpen(false);
    const prevPaId = overview?.program_assignment_id;
    setRegenerating(true);
    try {
      await api.post('/nutrition/regenerate');
      toast.info('Building your fresh 4-week plan… this takes about a minute.');
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const r = await api.get('/nutrition/my-program');
          const done = r.data && !r.data.regenerating && r.data.program_assignment_id !== prevPaId;
          if (done || Date.now() - started > 150000) {
            clearInterval(poll);
            setRegenerating(false);
            await reload?.();
            await loadOverview();
            if (done) toast.success('Your new meal plan is ready — enjoy the fresh variety!');
            else toast.error('Regeneration is taking longer than expected. Please refresh shortly.');
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (err) {
      setRegenerating(false);
      toast.error(err.response?.data?.detail || 'Could not start regeneration.');
    }
  };

  // group active-program assignments by week_number
  const weeks = useMemo(() => {
    const paId = overview?.program_assignment_id;
    let list = assignments || [];
    if (paId) {
      const filtered = list.filter(a => a.program_assignment_id === paId);
      if (filtered.length) list = filtered;
    }
    const map = {};
    list.forEach(a => {
      const w = a.week_number || 1;
      (map[w] = map[w] || []).push(a);
    });
    Object.values(map).forEach(arr => arr.sort((x, y) => (x.day_index || 0) - (y.day_index || 0)));
    return map;
  }, [assignments, overview]);

  if (!overview) return <div className="regen-card-elevated p-6 text-center text-sm text-muted-foreground">Loading program…</div>;

  const recCard = <MealRecommendation onChanged={async () => { await reload?.(); await loadOverview(); }} />;
  if (!overview.has_program || Object.keys(weeks).length === 0) {
    return (
      <div className="space-y-3">
        {recCard}
        <div className="regen-card-elevated p-8 text-center text-muted-foreground" data-testid="no-program">
          <UtensilsCrossed className="w-8 h-8 mx-auto mb-3 text-[#FFB800]" />
          No structured meal plan yet. Use <span className="text-[#FFB800] font-semibold">Build a Meal</span> for full flexibility.
        </div>
      </div>
    );
  }

  const weekNums = Object.keys(weeks).map(Number).sort((a, b) => a - b);
  const lockedFrom = overview.locked_from_week || null;
  const isWeekLocked = (w) => lockedFrom != null && w >= lockedFrom;
  const rawActive = week && weeks[week] ? week : weekNums[0];
  const activeWeek = isWeekLocked(rawActive) ? (weekNums.find(w => !isWeekLocked(w)) || weekNums[0]) : rawActive;
  const days = weeks[activeWeek] || [];
  const dt = overview.daily_target || {};

  return (
    <div className="space-y-3" data-testid="program-browser">
      {recCard}
      {overview.needs_review && (
        <div className="rounded-xl border border-[#FFB800]/40 bg-[#FFB800]/10 p-3 text-xs" data-testid="plan-needs-review">
          A few days of this plan could not be matched exactly to your calorie or protein target. Your coach has been notified and is reviewing them.
        </div>
      )}
      <div className="regen-card-elevated p-4 bg-gradient-to-br from-[#FFB800]/10 to-transparent">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="label-mini text-[#FFB800]">Your 4-Week Plan</div>
            <div className="font-display font-black text-xl mt-0.5">{overview.name}</div>
            {overview.goal && <div className="text-xs text-muted-foreground mt-0.5">{overview.goal}</div>}
          </div>
          {overview.can_regenerate && (
            <button onClick={() => setConfirmOpen(true)} disabled={regenerating} data-testid="regenerate-plan-btn"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-[#FFB800] text-[#081726] text-xs font-bold active:scale-95 transition disabled:opacity-60">
              <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />
              {regenerating ? 'Regenerating…' : 'Regenerate'}
            </button>
          )}
        </div>
        {(dt.calories || dt.protein) > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap font-mono text-xs">
            <span className="pill bg-[#FFB800]/10 text-[#FFB800]">{Math.round(dt.calories || 0)} kcal/day</span>
            <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)]">P {Math.round(dt.protein || 0)}</span>
            <span className="pill bg-[#00C8FF]/10 text-[#00C8FF]">C {Math.round(dt.carbs || 0)}</span>
            <span className="pill bg-[#7DD3FC]/10 text-[#7DD3FC]">F {Math.round(dt.fat || 0)}</span>
          </div>
        )}
        <div className="mt-3 text-[11px] text-muted-foreground flex items-start gap-1.5" data-testid="raw-weight-note">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#7DD3FC]" />
          All ingredient weights are raw (uncooked) unless otherwise specified.
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-regen-card border-[var(--c-hair)]" data-testid="regenerate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate your meal plan?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll build a brand-new, freshly varied 4-week plan tailored to your goal and diet, and replace your current one. Your logged meals stay untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="regenerate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={startRegenerate} data-testid="regenerate-confirm-btn" className="bg-[#FFB800] text-[#081726] hover:bg-[#FFB800]/90">Regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Week selector */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="week-selector">
        {weekNums.map(w => {
          const locked = isWeekLocked(w);
          return (
            <button key={w} data-testid={`week-pill-${w}`}
              onClick={() => locked ? toast.info('Weeks 2–4 unlock when you subscribe to YourReGen+.') : setWeek(w)}
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition flex items-center gap-1.5 ${w === activeWeek ? 'bg-[#FFB800] text-[#081726]' : locked ? 'bg-[var(--c-film)] text-muted-foreground/50' : 'bg-[var(--c-film)] text-muted-foreground'}`}>
              {locked && <Lock className="w-3 h-3" />}
              Week {w}{w === overview.current_week ? ' · Now' : ''}
            </button>
          );
        })}
      </div>
      {lockedFrom && (
        <div className="text-[11px] text-muted-foreground -mt-1 flex items-center gap-1.5" data-testid="trial-week-note">
          <Info className="w-3.5 h-3.5 text-[#7DD3FC]" />
          Free trial includes all of Week 1. Subscribe to unlock your full personalized 4-week program.
        </div>
      )}

      <div className="space-y-2" data-testid="day-list">
        {days.map((d, i) => <DayCard key={d.id} assignment={d} isToday={d.date === today} onMealClick={(meal, slot) => setDetail({ meal, slot })} defaultOpen={d.date === today} />)}
      </div>

      <MealRecipeDetail data={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function DayCard({ assignment, isToday, onMealClick, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const snaps = assignment.meal_snapshots || [];
  const t = dayTotals(snaps);
  let dayName = '';
  try { const d = parseISO(assignment.date); dayName = DAY_FULL[(d.getDay() + 6) % 7]; } catch { dayName = `Day ${(assignment.day_index || 0) + 1}`; }
  const done = assignment.status === 'completed' || snaps.every(s => s.done);
  const eatenCount = snaps.filter(s => s.done).length;

  return (
    <div className={`regen-card-elevated overflow-hidden ${isToday ? 'ring-1 ring-[#FFB800]/40' : ''}`} data-testid={`day-card-${assignment.day_index}`}>
      <button onClick={() => setOpen(o => !o)} data-testid={`day-toggle-${assignment.day_index}`}
        className="w-full p-4 flex items-center gap-3 text-left active:scale-[0.995] transition">
        <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${done ? 'bg-[var(--c-green-soft)]' : 'bg-[#FFB800]/15'}`}>
          {done ? <Check className="w-4 h-4 text-[var(--c-green-ink)]" /> : <span className="font-display font-black text-xs text-[#FFB800]">{DAY_NAMES[(new Date(assignment.date).getDay() + 6) % 7] || ''}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm">{dayName}</div>
            {isToday && <span className="pill bg-[#FFB800]/15 text-[#FFB800] text-[9px]">TODAY</span>}
            {done && <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[9px]">DONE</span>}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            <Flame className="w-3 h-3 inline mr-0.5 text-[#FFB800]" />{Math.round(t.cal)} kcal · P{Math.round(t.p)} C{Math.round(t.c)} F{Math.round(t.f)} · {snaps.length} meals{eatenCount > 0 ? ` · ${eatenCount} eaten` : ''}
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {snaps.map((s, idx) => (
            <button key={idx} onClick={() => onMealClick(s.meal, s.slot)} data-testid={`day-meal-${assignment.day_index}-${idx}`}
              className={`w-full regen-card p-2.5 flex gap-3 items-center text-left active:scale-[0.99] transition ${s.done ? 'opacity-70' : ''}`}>
              <img src={mealImage(s.meal)} alt={s.meal?.name} onError={(e) => { e.currentTarget.src = GENERIC_MEAL_IMAGE; }} className="w-14 h-14 rounded-lg object-cover bg-[var(--c-film)] flex-shrink-0" loading="lazy" />
              <div className="flex-1 min-w-0">
                <div className="label-mini text-[#FFB800]">{s.slot}{s.meal?.meal_time ? ` · ${s.meal.meal_time}` : ''}</div>
                <div className={`text-sm font-medium truncate ${s.done ? 'line-through text-muted-foreground' : ''}`}>{s.meal?.name}</div>
                <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{Math.round(s.meal?.calories || 0)} kcal · P{Math.round(s.meal?.protein_g || 0)} C{Math.round(s.meal?.carbs_g || 0)} F{Math.round(s.meal?.fat_g || 0)}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MealRecipeDetail({ data, onClose }) {
  if (!data?.meal) return null;
  const m = data.meal;
  const ingredients = m.ingredients_list?.length ? m.ingredients_list : null;
  const steps = m.prep_steps || [];
  const subs = m.substitutions || [];
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md lg:max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="recipe-detail">
        <DialogHeader className="sr-only"><DialogTitle>{m.name}</DialogTitle></DialogHeader>
        <div className="relative">
          <img src={mealImage(m)} alt={m.name} onError={(e) => { e.currentTarget.src = GENERIC_MEAL_IMAGE; }} className="w-full h-48 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-regen-card via-transparent to-transparent" />
          <div className="absolute bottom-3 left-4 right-4">
            <div className="label-mini text-[#FFB800]">{data.slot}{m.meal_time ? ` · ${m.meal_time}` : ''}</div>
            <div className="font-display font-black text-2xl leading-tight">{m.name}</div>
          </div>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-4 gap-2">
            <Macro label="Cal" value={Math.round(m.calories || 0)} color="#FFB800" />
            <Macro label="P" value={`${Math.round(m.protein_g || 0)}g`} color='#65A30D' />
            <Macro label="C" value={`${Math.round(m.carbs_g || 0)}g`} color="#00C8FF" />
            <Macro label="F" value={`${Math.round(m.fat_g || 0)}g`} color="#7DD3FC" />
          </div>

          {m.cook_time_min ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5 text-[#FFB800]" /> {m.cook_time_min} min prep
              {m.fiber_g ? <span className="ml-2">· {Math.round(m.fiber_g)}g fiber</span> : null}
            </div>
          ) : null}

          {ingredients && (
            <div>
              <div className="label-mini mb-2">Ingredients <span className="text-muted-foreground/70 normal-case font-normal">· raw weights</span></div>
              <div className="space-y-1.5">
                {ingredients.map((ing, i) => (
                  <div key={i} className="flex items-center justify-between regen-card px-3 py-2" data-testid={`ingredient-${i}`}>
                    <span className="text-sm">{ing.item}</span>
                    {ing.grams != null && <span className="font-mono text-xs text-[#FFB800]">{ing.grams} g</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {steps.length > 0 && (
            <div>
              <div className="label-mini mb-2">Preparation</div>
              <ol className="space-y-2">
                {steps.map((st, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FFB800]/15 text-[#FFB800] text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-muted-foreground">{st}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {subs.length > 0 && (
            <div>
              <div className="label-mini mb-2 flex items-center gap-1.5"><Repeat className="w-3 h-3" /> Substitutions</div>
              <div className="flex flex-wrap gap-1.5">
                {subs.map((s, i) => <span key={i} className="pill bg-[#00C8FF]/10 text-[#00C8FF] text-[10px]">{s}</span>)}
              </div>
            </div>
          )}

          <Button onClick={onClose} variant="outline" className="w-full h-11 border-[var(--c-hair)]" data-testid="recipe-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

